import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { PageHeader, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Boxes, Package, AlertTriangle, History, Copy, Pencil, Trash2, PackagePlus, SlidersHorizontal, ClipboardList, ArrowLeftRight, Download, Upload, FileSpreadsheet } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import InventoryDashboard from "./InventoryDashboard";

const UNIT_LABEL = { piece: "pc", gram: "g", kilogram: "kg", millilitre: "ml", litre: "L", box: "box", pack: "pack", bottle: "btl", tube: "tube", roll: "roll", sheet: "sheet", pair: "pair" };
const TYPE_LABEL = { sell: "Sale Product", consumption: "Use Product" };
const UNITS = Object.keys(UNIT_LABEL);
const EMPTY = { name: "", sku: "", product_type: "consumption", category: "", brand: "", unit: "piece", pack_size: "", mrp: 0, purchase_price: 0, purchase_discount: 0, final_purchase_cost: 0, selling_price: 0, gst_percent: 0, incentive_percent: 0, consumption_qty: 0, consumption_unit: "piece", supplier: "", notes: "", status: "active", branch_ids: [] };

const fmtQty = (q, unit) => `${Number(q ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} ${UNIT_LABEL[unit] || ""}`.trim();

export default function Inventory() {
  const { user } = useAuth();
  const { branches, selected, setSelected, branchParam } = useBranch();
  const isAdmin = user?.role === "OWNER" || user?.role === "SUPER_ADMIN";
  const isOwner = user?.role === "OWNER";

  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [lowOnly, setLowOnly] = useState(false);
  const [setupOnly, setSetupOnly] = useState(false);

  const [form, setForm] = useState(null);       // add/edit dialog
  const [editId, setEditId] = useState(null);
  const [stockDlg, setStockDlg] = useState(null); // {product, mode}
  const [histDlg, setHistDlg] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [recipeDlg, setRecipeDlg] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [services, setServices] = useState([]);
  const [newRecipe, setNewRecipe] = useState({ service_id: "", quantity: "", unit: "gram" });
  const [allServices, setAllServices] = useState([]);
  const [formServices, setFormServices] = useState({});
  const [bulkType, setBulkType] = useState("");
  const [bulkUnit, setBulkUnit] = useState("");
  const [transferDlg, setTransferDlg] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = () => {
    const params = { branch_id: branchParam, status: statusFilter };
    if (typeFilter !== "all") params.product_type = typeFilter;
    if (lowOnly) params.low_stock = true;
    if (setupOnly) params.needs_setup = true;
    if (q) params.q = q;
    api.get("/inventory/products", { params }).then((r) => setProducts(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/inventory/dashboard", { params: { branch_id: branchParam } }).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchParam, typeFilter, statusFilter, lowOnly, setupOnly]);
  useEffect(() => { loadTransfers(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return products.filter((p) => !t || [p.name, p.brand, p.category, p.sku].some((f) => (f || "").toLowerCase().includes(t)));
  }, [products, q]);

  const stockBranch = branchParam || (branches[0]?.id);

  const exportStock = async () => {
    try {
      const params = { branch_id: branchParam || undefined };
      if (typeFilter !== "all") params.product_type = typeFilter;
      const res = await api.get("/inventory/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "inventory_export.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Inventory exported");
    } catch { toast.error("Export failed"); }
  };

  const reorderExport = async () => {
    try {
      const res = await api.get("/inventory/reorder-list.pdf", { params: { branch_id: branchParam || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "reorder_list.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Reorder list exported");
    } catch { toast.error("Export failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/inventory/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "inventory_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a CSV or Excel file"); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await api.post("/inventory/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(`Imported: ${res.data.created} new, ${res.data.updated} updated, ${res.data.stock_set} stock rows`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  // ---- master save ----
  const loadFormServices = async (productId, unit) => {
    setAllServices([]); setFormServices({});
    try {
      const [svc, rec] = await Promise.all([
        api.get("/services", { params: { branch_id: branchParam } }),
        productId ? api.get("/inventory/recipes", { params: { product_id: productId } }) : Promise.resolve({ data: [] }),
      ]);
      const map = {};
      rec.data.forEach((r) => { map[r.service_id] = { checked: true, quantity: r.quantity || "", unit: r.unit || unit || "gram" }; });
      // show active services + any already-mapped (even if now inactive) so nothing is silently dropped
      const list = svc.data.filter((s) => s.status !== "inactive" || map[s.id]);
      setAllServices(list); setFormServices(map);
    } catch { setAllServices([]); }
  };
  const openNew = () => { setForm({ ...EMPTY }); setEditId(null); loadFormServices(null, "gram"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); loadFormServices(p.id, p.unit); };
  const toggleFormSvc = (sid, unit) => setFormServices((m) => ({ ...m, [sid]: m[sid]?.checked ? { ...m[sid], checked: false } : { checked: true, quantity: m[sid]?.quantity || "", unit: m[sid]?.unit || unit || "gram" } }));
  const setFormSvcField = (sid, field, val) => setFormServices((m) => ({ ...m, [sid]: { ...m[sid], [field]: val } }));
  const saveMaster = async () => {
    if (!form.name.trim()) { toast.error("Product name is required"); return; }
    try {
      const payload = { ...form, mrp: Number(form.mrp || 0), purchase_price: Number(form.purchase_price || 0), purchase_discount: Number(form.purchase_discount || 0), selling_price: Number(form.selling_price || 0), gst_percent: Number(form.gst_percent || 0), incentive_percent: Number(form.incentive_percent || 0) };
      let pid = editId;
      if (editId) await api.put(`/inventory/products/${editId}`, payload);
      else { const res = await api.post("/inventory/products", payload); pid = res.data.id; }
      if (form.product_type === "consumption" && pid) {
        const mappings = Object.entries(formServices).filter(([, v]) => v.checked)
          .map(([service_id, v]) => ({ service_id, quantity: Number(v.quantity || 0), unit: v.unit || "gram" }));
        await api.put(`/inventory/products/${pid}/services`, { mappings });
      }
      toast.success(editId ? "Product updated" : "Product added");
      setForm(null); setEditId(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doAction = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const deactivate = (p) => doAction(() => api.post(`/inventory/products/${p.id}/deactivate`), "Deactivated");
  const activate = (p) => doAction(() => api.post(`/inventory/products/${p.id}/activate`), "Activated");
  const duplicate = (p) => doAction(() => api.post(`/inventory/products/${p.id}/duplicate`), "Duplicated");
  const del = (p) => { if (!window.confirm(`Delete "${p.name}"? Products with history will be deactivated instead.`)) return; doAction(() => api.delete(`/inventory/products/${p.id}`), "Done"); };

  // ---- stock ops ----
  const openStock = (product, mode) => setStockDlg({ product, mode, branch_id: stockBranch, quantity: "", new_quantity: "", reason: "", min_stock: product.min_stock ?? 0 });
  const submitStock = async () => {
    const d = stockDlg; const pid = d.product.id;
    try {
      if (d.mode === "purchase") await api.post(`/inventory/products/${pid}/purchase`, { branch_id: d.branch_id, quantity: Number(d.quantity || 0), reason: d.reason });
      else if (d.mode === "opening") await api.post(`/inventory/products/${pid}/opening`, { branch_id: d.branch_id, quantity: Number(d.quantity || 0), reason: d.reason });
      else if (d.mode === "adjust") await api.post(`/inventory/products/${pid}/adjust`, { branch_id: d.branch_id, new_quantity: Number(d.new_quantity), reason: d.reason });
      else if (d.mode === "min") await api.put(`/inventory/products/${pid}/min-stock`, { branch_id: d.branch_id, min_stock: Number(d.min_stock || 0) });
      toast.success("Stock updated"); setStockDlg(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadTransfers = () => { if (!isOwner) return; api.get("/inventory/transfers").then((r) => setTransfers(r.data)).catch(() => {}); };
  const openTransfer = (product) => setTransferDlg({ product, from_branch: stockBranch, to_branch: "", quantity: "", reason: "" });
  const submitTransfer = async () => {
    const d = transferDlg;
    if (!d.from_branch || !d.to_branch) { toast.error("Select both branches"); return; }
    if (d.from_branch === d.to_branch) { toast.error("Source and destination must differ"); return; }
    if (!(Number(d.quantity) > 0)) { toast.error("Enter a valid quantity"); return; }
    try {
      await api.post("/inventory/transfer", { product_id: d.product.id, from_branch: d.from_branch, to_branch: d.to_branch, quantity: Number(d.quantity), reason: d.reason });
      toast.success("Stock transferred"); setTransferDlg(null); load(); loadTransfers();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openHistory = async (p) => {
    setHistDlg(p);
    try { const { data } = await api.get(`/inventory/products/${p.id}/history`, { params: { branch_id: branchParam } }); setHistory(data); } catch { setHistory([]); }
  };

  const openRecipe = async (p) => {
    setRecipeDlg(p); setNewRecipe({ service_id: "", quantity: "", unit: p.unit || "gram" });
    try {
      const [svc, rec] = await Promise.all([
        api.get("/services", { params: { branch_id: branchParam } }),
        api.get("/inventory/recipes", { params: { product_id: p.id } }),
      ]);
      setServices(svc.data); setRecipes(rec.data);
    } catch { setServices([]); setRecipes([]); }
  };
  const addRecipe = async () => {
    if (!newRecipe.service_id) { toast.error("Pick a service"); return; }
    const svc = services.find((s) => s.id === newRecipe.service_id);
    try {
      await api.post("/inventory/recipes", { service_id: newRecipe.service_id, service_name: svc?.name, product_id: recipeDlg.id, quantity: Number(newRecipe.quantity), unit: newRecipe.unit });
      const { data } = await api.get("/inventory/recipes", { params: { product_id: recipeDlg.id } });
      setRecipes(data); setNewRecipe({ service_id: "", quantity: "", unit: recipeDlg.unit || "gram" }); toast.success("Consumption added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delRecipe = async (rid) => {
    try { await api.delete(`/inventory/recipes/${rid}`); setRecipes((r) => r.filter((x) => x.id !== rid)); toast.success("Removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- bulk setup ----
  const toggleSel = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const applyBulk = async () => {
    if (!selectedIds.length || (!bulkType && !bulkUnit)) { toast.error("Select products and a type/unit"); return; }
    try { await api.post("/inventory/bulk-setup", { product_ids: selectedIds, product_type: bulkType || null, unit: bulkUnit || null }); toast.success(`Updated ${selectedIds.length} product(s)`); setSelectedIds([]); setBulkType(""); setBulkUnit(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const Stat = ({ label, value, icon: Icon, tone }) => (
    <Card className="p-4 bg-card border-border" data-testid={`inv-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon size={16} className={tone || "text-primary"} />
      </div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHeader title="Inventory" subtitle="Product Master & branch-wise stock"
        actions={
          <div className="flex flex-wrap gap-2">
            {isAdmin && <Button onClick={reorderExport} variant="outline" className="rounded-sm border-red-800 text-red-300 hover:bg-red-900/20" data-testid="inv-reorder-btn"><AlertTriangle size={15} className="mr-1" /> Reorder List</Button>}
            {isAdmin && <Button onClick={exportStock} variant="outline" className="rounded-sm" data-testid="inv-export-btn"><Download size={15} className="mr-1" /> Export</Button>}
            {isOwner && <Button onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }} variant="outline" className="rounded-sm" data-testid="inv-import-btn"><Upload size={15} className="mr-1" /> Import</Button>}
            {isOwner && <Button onClick={openNew} data-testid="inv-add-product" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Product</Button>}
          </div>
        } />

      {/* Branch selector for admins */}
      {isAdmin && (
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Branch:</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-64 h-9 bg-secondary" data-testid="inv-branch-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {isOwner && <SelectItem value="all">All Branches (consolidated)</SelectItem>}
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="inv-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="products" data-testid="inv-tab-products">Products</TabsTrigger>
          {isOwner && <TabsTrigger value="transfers" data-testid="inv-tab-transfers">Transfers</TabsTrigger>}
        </TabsList>
        <TabsContent value="dashboard"><InventoryDashboard /></TabsContent>
        <TabsContent value="products" className="space-y-6">

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Total" value={stats.total_products ?? 0} icon={Boxes} />
        <Stat label="Sell" value={stats.sell_products ?? 0} icon={Package} />
        <Stat label="Consumption" value={stats.consumption_products ?? 0} icon={Package} />
        <Stat label="Low Stock" value={stats.low_stock_products ?? 0} icon={AlertTriangle} tone="text-orange-300" />
        <Stat label="Out of Stock" value={stats.out_of_stock_products ?? 0} icon={AlertTriangle} tone="text-red-400" />
        <Stat label="Stock Value" value={rupee(stats.total_stock_value ?? 0)} icon={Boxes} />
      </div>

      {/* Filters */}
      <Card className="p-3 bg-card border-border">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, brand, SKU…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-secondary" data-testid="inv-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-40 h-9 bg-secondary" data-testid="inv-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="sell">Sell</SelectItem><SelectItem value="consumption">Consumption</SelectItem></SelectContent></Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-36 h-9 bg-secondary" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
          <Button variant={lowOnly ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => setLowOnly((v) => !v)} data-testid="inv-low-filter"><AlertTriangle size={14} className="mr-1" /> Low Stock</Button>
          <Button variant={setupOnly ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => setSetupOnly((v) => !v)} data-testid="inv-setup-filter"><SlidersHorizontal size={14} className="mr-1" /> Needs Setup</Button>
        </div>
      </Card>

      {/* Bulk setup toolbar */}
      {isOwner && selectedIds.length > 0 && (
        <Card className="p-3 bg-primary/10 border-primary/30" data-testid="inv-bulk-bar">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{selectedIds.length} selected</span>
            <Select value={bulkType} onValueChange={setBulkType}><SelectTrigger className="w-40 h-9 bg-secondary" data-testid="inv-bulk-type"><SelectValue placeholder="Set type…" /></SelectTrigger>
              <SelectContent><SelectItem value="sell">Sell</SelectItem><SelectItem value="consumption">Consumption</SelectItem></SelectContent></Select>
            <Select value={bulkUnit} onValueChange={setBulkUnit}><SelectTrigger className="w-40 h-9 bg-secondary" data-testid="inv-bulk-unit"><SelectValue placeholder="Set unit…" /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
            <Button size="sm" className="rounded-sm bg-primary text-primary-foreground" onClick={applyBulk} data-testid="inv-bulk-apply">Apply</Button>
            <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setSelectedIds([])}>Clear</Button>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {isOwner && <TableHead className="w-8"></TableHead>}
                <TableHead>Product</TableHead><TableHead>Type</TableHead><TableHead>Unit</TableHead>
                <TableHead className="text-right">Current</TableHead><TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Purchase</TableHead><TableHead className="text-right">Selling</TableHead>
                <TableHead className="text-right">Incentive</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} className="border-border" data-testid={`inv-row-${p.id}`}>
                  {isOwner && <TableCell><Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`inv-check-${p.id}`} /></TableCell>}
                  <TableCell className="font-medium">
                    {p.name}
                    {p.needs_setup && <Badge variant="outline" className="ml-2 text-[10px] border-orange-400/50 text-orange-300">NEEDS SETUP</Badge>}
                    {p.brand && <div className="text-xs text-muted-foreground">{p.brand}{p.category ? ` · ${p.category}` : ""}</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={p.product_type === "sell" ? "border-emerald-400/40 text-emerald-300" : "border-sky-400/40 text-sky-300"}>{TYPE_LABEL[p.product_type] || "Use Product"}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{p.unit}</TableCell>
                  <TableCell className="text-right" data-testid={`inv-stock-${p.id}`}>
                    <span className={p.low_stock ? "text-orange-300 font-semibold" : ""}>{fmtQty(p.current_stock, p.base_unit)}</span>
                    {p.low_stock && <Badge className="ml-1 bg-orange-500/20 text-orange-300 text-[10px]">LOW</Badge>}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtQty(p.min_stock, p.base_unit)}</TableCell>
                  <TableCell className="max-w-[180px]"><span className="text-xs text-muted-foreground line-clamp-2" data-testid={`inv-services-${p.id}`}>{p.service_names || "—"}</span></TableCell>
                  <TableCell className="text-right text-muted-foreground">{p.purchase_price ? rupee(p.purchase_price) : "—"}</TableCell>
                  <TableCell className="text-right text-primary">{p.product_type === "sell" && p.selling_price ? rupee(p.selling_price) : "—"}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{p.product_type === "sell" ? `${p.incentive_percent || 0}%` : "—"}</TableCell>
                  <TableCell>{p.status === "inactive" ? <Badge variant="outline" className="text-muted-foreground">Inactive</Badge> : <Badge className="bg-green-500/15 text-green-300">Active</Badge>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8" title="Add Purchase" onClick={() => openStock(p, "purchase")} data-testid={`inv-purchase-${p.id}`}><PackagePlus size={15} /></Button>}
                    {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8" title="Stock Adjustment" onClick={() => openStock(p, "adjust")} data-testid={`inv-adjust-${p.id}`}><SlidersHorizontal size={15} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-300" title="Transfer to another branch" onClick={() => openTransfer(p)} data-testid={`inv-transfer-${p.id}`}><ArrowLeftRight size={15} /></Button>}
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="History" onClick={() => openHistory(p)} data-testid={`inv-history-${p.id}`}><History size={15} /></Button>
                    {isOwner && p.product_type === "consumption" && <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-300" title="Set Consumption" onClick={() => openRecipe(p)} data-testid={`inv-recipe-${p.id}`}><ClipboardList size={15} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => openEdit(p)} data-testid={`inv-edit-${p.id}`}><Pencil size={15} /></Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8" title="Duplicate" onClick={() => duplicate(p)} data-testid={`inv-dup-${p.id}`}><Copy size={15} /></Button>}
                    {p.status === "inactive"
                      ? <Button size="sm" variant="ghost" className="h-8 text-green-300" onClick={() => activate(p)} data-testid={`inv-activate-${p.id}`}>Activate</Button>
                      : <Button size="sm" variant="ghost" className="h-8 text-orange-300" onClick={() => deactivate(p)} data-testid={`inv-deactivate-${p.id}`}>Off</Button>}
                    {isOwner && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Delete" onClick={() => del(p)} data-testid={`inv-del-${p.id}`}><Trash2 size={15} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && <TableRow><TableCell colSpan={isOwner ? 12 : 11} className="text-center text-muted-foreground py-10">No products found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
        </TabsContent>
        {isOwner && (
        <TabsContent value="transfers">
          <Card className="bg-card border-border overflow-x-auto" data-testid="inv-transfers-table">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead>Date</TableHead><TableHead>Product</TableHead><TableHead>From</TableHead><TableHead>To</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead>By</TableHead><TableHead>Reason</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {transfers.map((t) => (
                  <TableRow key={t.id} className="border-border text-sm" data-testid={`inv-transfer-row-${t.id}`}>
                    <TableCell className="whitespace-nowrap">{(t.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell>{t.product_name}</TableCell>
                    <TableCell className="text-orange-300">{t.from_branch_name}</TableCell>
                    <TableCell className="text-green-300">{t.to_branch_name}</TableCell>
                    <TableCell className="text-right">{t.quantity} {UNIT_LABEL[t.unit] || t.unit}</TableCell>
                    <TableCell>{t.user_name}</TableCell>
                    <TableCell className="text-muted-foreground">{t.reason || "—"}</TableCell>
                  </TableRow>
                ))}
                {!transfers.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No transfers yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
        )}
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3 text-sm max-h-[65vh] overflow-y-auto pr-1">
              <div className="col-span-2"><Label className="text-xs">Product Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-name" disabled={!isOwner && !!editId} /></div>
              <div><Label className="text-xs">Product Type *</Label>
                <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })} disabled={!isOwner && !!editId}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="inv-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="sell">Sale Product</SelectItem><SelectItem value="consumption">Use Product / Consumption</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Unit *</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="inv-form-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">SKU / Code</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-secondary mt-1" disabled={!isOwner && !!editId} /></div>
              <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-secondary mt-1" /></div>
              <div><Label className="text-xs">Pack Size</Label><Input value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} className="bg-secondary mt-1" placeholder="e.g. 500 g" /></div>
              {isOwner && <>
                <div><Label className="text-xs">MRP ₹</Label><Input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-mrp" /></div>
                <div><Label className="text-xs">Purchase Price ₹</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-purchase" /></div>
                <div><Label className="text-xs">Purchase Discount ₹</Label><Input type="number" value={form.purchase_discount} onChange={(e) => setForm({ ...form, purchase_discount: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-discount" /></div>
                <div><Label className="text-xs">Final Purchase Cost ₹</Label><Input readOnly value={(Number(form.purchase_price || 0) - Number(form.purchase_discount || 0)).toFixed(2)} className="bg-secondary/50 mt-1 text-primary font-medium" data-testid="inv-form-finalcost" /></div>
                {form.product_type === "sell" && <>
                  <div><Label className="text-xs">Selling Price ₹</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-selling" /></div>
                  <div><Label className="text-xs">GST %</Label><Input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="bg-secondary mt-1" /></div>
                  <div><Label className="text-xs">Staff Incentive %</Label><Input type="number" value={form.incentive_percent} onChange={(e) => setForm({ ...form, incentive_percent: e.target.value })} className="bg-secondary mt-1" data-testid="inv-form-incentive" /></div>
                </>}
              </>}
              <div><Label className="text-xs">Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="bg-secondary mt-1" /></div>
              <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-secondary mt-1" /></div>
              {form.product_type === "consumption" && (
                <div className="col-span-2 border-t border-border pt-3" data-testid="inv-form-services">
                  <Label className="text-sm font-medium">Used in Which Services?</Label>
                  <p className="text-xs text-muted-foreground mb-2">Tick services that use this product. Quantity is optional — leave blank to link without auto-deduction; enter a value to auto-deduct per service.</p>
                  <div className="max-h-56 overflow-y-auto space-y-1 rounded-md border border-border p-2">
                    {allServices.length === 0 && <div className="text-xs text-muted-foreground py-3 text-center">Loading services…</div>}
                    {allServices.map((s) => {
                      const fs = formServices[s.id] || {};
                      return (
                        <div key={s.id} className="flex items-center gap-2" data-testid={`inv-svc-opt-${s.id}`}>
                          <Checkbox checked={!!fs.checked} onCheckedChange={() => toggleFormSvc(s.id, form.unit)} data-testid={`inv-svc-check-${s.id}`} />
                          <span className="flex-1 text-sm truncate">{s.name}{s.status === "inactive" ? " (inactive)" : ""}</span>
                          {fs.checked && <>
                            <Input type="number" step="any" placeholder="qty" value={fs.quantity} onChange={(e) => setFormSvcField(s.id, "quantity", e.target.value)} className="bg-secondary h-8 w-20" data-testid={`inv-svc-qty-${s.id}`} />
                            <Select value={fs.unit || form.unit} onValueChange={(v) => setFormSvcField(s.id, "unit", v)}>
                              <SelectTrigger className="bg-secondary h-8 w-24" data-testid={`inv-svc-unit-${s.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
                          </>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={saveMaster} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-form-save">{editId ? "Save Changes" : "Add Product"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock op dialog */}
      <Dialog open={!!stockDlg} onOpenChange={(o) => !o && setStockDlg(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl capitalize">{stockDlg?.mode === "min" ? "Set Minimum Stock" : stockDlg?.mode === "adjust" ? "Stock Adjustment" : stockDlg?.mode === "opening" ? "Opening Stock" : "Add Purchase"}</DialogTitle></DialogHeader>
          {stockDlg && (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">{stockDlg.product.name} · current {fmtQty(stockDlg.product.current_stock, stockDlg.product.base_unit)}</div>
              {isAdmin && (
                <div><Label className="text-xs">Branch</Label>
                  <Select value={stockDlg.branch_id} onValueChange={(v) => setStockDlg({ ...stockDlg, branch_id: v })}>
                    <SelectTrigger className="bg-secondary mt-1" data-testid="inv-stock-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              )}
              {(stockDlg.mode === "purchase" || stockDlg.mode === "opening") && (
                <div><Label className="text-xs">Quantity ({stockDlg.product.unit})</Label><Input type="number" step="any" value={stockDlg.quantity} onChange={(e) => setStockDlg({ ...stockDlg, quantity: e.target.value })} className="bg-secondary mt-1" data-testid="inv-stock-qty" /></div>
              )}
              {stockDlg.mode === "adjust" && (
                <div><Label className="text-xs">New Quantity ({stockDlg.product.unit})</Label><Input type="number" step="any" value={stockDlg.new_quantity} onChange={(e) => setStockDlg({ ...stockDlg, new_quantity: e.target.value })} className="bg-secondary mt-1" data-testid="inv-stock-newqty" /></div>
              )}
              {stockDlg.mode === "min" && (
                <div><Label className="text-xs">Minimum Stock ({stockDlg.product.unit})</Label><Input type="number" step="any" value={stockDlg.min_stock} onChange={(e) => setStockDlg({ ...stockDlg, min_stock: e.target.value })} className="bg-secondary mt-1" /></div>
              )}
              {stockDlg.mode !== "min" && (
                <div><Label className="text-xs">Reason {stockDlg.mode === "adjust" && "*"}</Label><Input value={stockDlg.reason} onChange={(e) => setStockDlg({ ...stockDlg, reason: e.target.value })} className="bg-secondary mt-1" placeholder="Recorded in ledger" data-testid="inv-stock-reason" /></div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={submitStock} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-stock-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!histDlg} onOpenChange={(o) => !o && setHistDlg(null)}>
        <DialogContent className="bg-card border-border max-w-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">Stock History — {histDlg?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Change</TableHead>
                <TableHead className="text-right">Prev</TableHead><TableHead className="text-right">New</TableHead><TableHead>User</TableHead><TableHead>Reason</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id} className="border-border text-xs" data-testid={`inv-ledger-${h.id}`}>
                    <TableCell className="whitespace-nowrap">{(h.created_at || "").slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="capitalize">{(h.txn_type || "").replace(/_/g, " ")}</TableCell>
                    <TableCell className={`text-right ${h.quantity >= 0 ? "text-green-300" : "text-orange-300"}`}>{h.quantity >= 0 ? "+" : ""}{h.quantity}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{h.previous_stock}</TableCell>
                    <TableCell className="text-right">{h.new_stock}</TableCell>
                    <TableCell>{h.user_name} <span className="text-muted-foreground">({h.user_role})</span></TableCell>
                    <TableCell className="text-muted-foreground">{h.reason}</TableCell>
                  </TableRow>
                ))}
                {!history.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No movements yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Consumption Recipe dialog */}
      <Dialog open={!!recipeDlg} onOpenChange={(o) => !o && setRecipeDlg(null)}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">Service Consumption — {recipeDlg?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Set how much of this product each service consumes. Deducted automatically when a service bill is finalized.</p>
          <div className="max-h-[45vh] overflow-y-auto mt-2">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead>Service</TableHead><TableHead className="text-right">Consumption</TableHead><TableHead>Unit</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {recipes.map((r) => (
                  <TableRow key={r.id} className="border-border" data-testid={`inv-recipe-row-${r.id}`}>
                    <TableCell>{r.service_name}</TableCell>
                    <TableCell className="text-right">{r.quantity}</TableCell>
                    <TableCell>{r.unit}</TableCell>
                    <TableCell className="text-right"><Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delRecipe(r.id)} data-testid={`inv-recipe-del-${r.id}`}><Trash2 size={14} /></Button></TableCell>
                  </TableRow>
                ))}
                {!recipes.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No services linked yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
            <div className="flex-1 min-w-[180px]"><Label className="text-xs">Service</Label>
              <Select value={newRecipe.service_id} onValueChange={(v) => setNewRecipe({ ...newRecipe, service_id: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="inv-recipe-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="w-28"><Label className="text-xs">Qty</Label><Input type="number" step="any" value={newRecipe.quantity} onChange={(e) => setNewRecipe({ ...newRecipe, quantity: e.target.value })} className="bg-secondary mt-1" data-testid="inv-recipe-qty" /></div>
            <div className="w-32"><Label className="text-xs">Unit</Label>
              <Select value={newRecipe.unit} onValueChange={(v) => setNewRecipe({ ...newRecipe, unit: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="inv-recipe-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={addRecipe} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-recipe-add"><Plus size={15} className="mr-1" /> Add</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Stock Transfer dialog */}
      <Dialog open={!!transferDlg} onOpenChange={(o) => !o && setTransferDlg(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="inv-transfer-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Stock Transfer</DialogTitle>
            <DialogDescription className="sr-only">Move stock of this product from one branch to another with an audit trail.</DialogDescription>
          </DialogHeader>
          {transferDlg && (
            <div className="space-y-3 text-sm">
              <div className="text-muted-foreground">{transferDlg.product.name} · unit {transferDlg.product.unit}</div>
              <div><Label className="text-xs">From Branch</Label>
                <Select value={transferDlg.from_branch} onValueChange={(v) => setTransferDlg({ ...transferDlg, from_branch: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="inv-transfer-from"><SelectValue placeholder="Source branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">To Branch</Label>
                <Select value={transferDlg.to_branch} onValueChange={(v) => setTransferDlg({ ...transferDlg, to_branch: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="inv-transfer-to"><SelectValue placeholder="Destination branch" /></SelectTrigger>
                  <SelectContent>{branches.filter((b) => b.id !== transferDlg.from_branch).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Quantity ({transferDlg.product.unit})</Label><Input type="number" step="any" value={transferDlg.quantity} onChange={(e) => setTransferDlg({ ...transferDlg, quantity: e.target.value })} className="bg-secondary mt-1" data-testid="inv-transfer-qty" /></div>
              <div><Label className="text-xs">Reason / Note</Label><Input value={transferDlg.reason} onChange={(e) => setTransferDlg({ ...transferDlg, reason: e.target.value })} className="bg-secondary mt-1" placeholder="Recorded in audit trail" data-testid="inv-transfer-reason" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={submitTransfer} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-transfer-save">Transfer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Import Products & Stock</DialogTitle>
            <DialogDescription>Upload a CSV or Excel file. Use the template so columns match. Opening stock is set per branch (branch-wise, isolated).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button onClick={downloadTemplate} variant="outline" className="rounded-sm w-full" data-testid="inv-import-template"><FileSpreadsheet size={15} className="mr-1" /> Download CSV Template</Button>
            <div>
              <Label className="text-xs">Choose file (.csv / .xlsx)</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="bg-secondary mt-1" data-testid="inv-import-file" />
            </div>
            {importResult && (
              <div className="text-sm bg-secondary rounded-sm p-3 space-y-1" data-testid="inv-import-result">
                <p className="text-primary">✓ {importResult.created} created · {importResult.updated} updated · {importResult.stock_set} stock rows set</p>
                {(importResult.errors || []).map((e, i) => <p key={i} className="text-destructive text-xs">{e}</p>)}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={runImport} disabled={importing || !importFile} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="inv-import-run">{importing ? "Importing…" : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
